import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { viewProfileAsync, selectUserProfile, selectProfileLoading, selectProfileError } from '../features/wholesaler/wholesalerSlice';
import { updateUserAsync } from '../features/auth/authSlice';

const WholesalerProfile = () => {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const username = userData.username;
  const dispatch = useDispatch();
  const userProfile = useSelector(selectUserProfile);
  const loading = useSelector(selectProfileLoading);
  const error = useSelector(selectProfileError);
  const { approvedConnections } = useSelector((state) => state.connections);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    address: '',
    city: '',
    pincode: '',
    phone: ''
  });

  useEffect(() => {
    dispatch(viewProfileAsync(username)).unwrap();
  }, [dispatch]);

  useEffect(() => {
    if (userProfile) {
      setEditedProfile({
        address: userProfile.address || '',
        city: userProfile.city || '',
        pincode: userProfile.pincode || '',
        phone: userProfile.phoneNumber || ''
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      const updatedData = {
        username: userProfile.username,
        address: editedProfile.address,
        city: editedProfile.city,
        pincode: editedProfile.pincode,
        phoneNumber: editedProfile.phone
      };

      await dispatch(updateUserAsync(updatedData)).unwrap();
      
      // Refresh the profile data
      await dispatch(viewProfileAsync(username)).unwrap();
      
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    setEditedProfile({
      address: userProfile?.address || '',
      city: userProfile?.city || '',
      pincode: userProfile?.pincode || '',
      phone: userProfile?.phoneNumber || ''
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WholesalerNavbar />
        <div className="pt-20 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (error || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WholesalerNavbar />
        <div className="pt-20 flex justify-center items-center">
          <div className="text-red-500">
            {error || 'Failed to load profile data'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WholesalerNavbar />
      <div className="pt-20 max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Profile Details</h2>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <FaEdit className="mr-2" />
                  Edit Profile
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <FaTimes className="mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <FaSave className="mr-2" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Non-editable fields */}
              <div className="bg-gray-100 p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700">
                  Username
                </label>
                <div className="mt-1">
                  <p className="text-gray-900">{userProfile.userName}</p>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700">
                  Shop Name
                </label>
                <div className="mt-1">
                  <p className="text-gray-900">{userProfile.registeredFirmName}</p>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700">
                  Owner Name
                </label>
                <div className="mt-1">
                  <p className="text-gray-900">{userProfile.ownerName}</p>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <p className="text-gray-900">{userProfile.email}</p>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700">
                  Total Connections
                </label>
                <div className="mt-1">
                  <p className="text-gray-900">{userProfile.approvedConnections}</p>
                </div>
              </div>

              {/* Editable fields */}
              <div className="bg-white p-4 rounded-md shadow-md">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                {isEditing ? (
                  <textarea
                    name="address"
                    id="address"
                    rows={3}
                    value={editedProfile.address}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  />
                ) : (
                  <div className="mt-1">
                    <p className="text-gray-900">{userProfile.address}</p>
                  </div>
                )}
              </div>

              <div className="bg-white p-4 rounded-md shadow-md">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="city"
                    id="city"
                    value={editedProfile.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  />
                ) : (
                  <div className="mt-1">
                    <p className="text-gray-900">{userProfile.city}</p>
                  </div>
                )}
              </div>

              <div className="bg-white p-4 rounded-md shadow-md">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    value={editedProfile.phone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  />
                ) : (
                  <div className="mt-1">
                    <p className="text-gray-900">{userProfile.phoneNumber}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesalerProfile;